'use client';

import { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { Card, CardContent } from '@myapp/ui/components/card';
import { Badge } from '@myapp/ui/components/badge';
import { Button } from '@myapp/ui/components/button';

interface Recipe {
  id: number;
  title: string;
  category: string;
  time: string;
  difficulty: string;
  image: string;
  description?: string;
}

interface TrendingRecipeCarouselProps {
  recipes: Recipe[];
}

export function TrendingRecipeCarousel({ recipes }: TrendingRecipeCarouselProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const itemsPerView = 4;
  const maxSlides = Math.max(0, recipes.length - itemsPerView);

  const nextSlide = () => {
    if (currentSlide < maxSlides) {
      setCurrentSlide(currentSlide + 1);
    } else {
      setCurrentSlide(0);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    } else {
      setCurrentSlide(maxSlides);
    }
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(Math.min(index, maxSlides));
  };

  return (
    <div className="relative">
      {/* Navigation Buttons */}
      <div className="absolute -top-16 right-0 flex gap-2 z-10">
        <Button
          variant="outline"
          size="sm"
          onClick={prevSlide}
          className="rounded-full w-10 h-10 p-0 hover:bg-purple-50 border-purple-200"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={nextSlide}
          className="rounded-full w-10 h-10 p-0 hover:bg-purple-50 border-purple-200"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Progress Indicator */}
      <div className="flex justify-center mb-8">
        <div className="flex gap-2">
          {Array.from({ length: maxSlides + 1 }, (_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                currentSlide === index 
                  ? 'bg-purple-500 w-8' 
                  : 'bg-purple-200 hover:bg-purple-300'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Carousel Container */}
      <div className="overflow-hidden" ref={containerRef}>
        <div 
          className="flex transition-transform duration-500 ease-in-out"
          style={{
            transform: `translateX(-${currentSlide * (100 / itemsPerView)}%)`
          }}
        >
          {recipes.map((recipe) => (
            <div 
              key={recipe.id} 
              className="flex-shrink-0"
              style={{ width: `${100 / itemsPerView}%` }}
            >
              <div className="px-3">
                <Card className="group hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden transform hover:-translate-y-1">
                  <div className="aspect-video bg-gradient-to-br from-orange-100 to-yellow-100 flex items-center justify-center text-5xl relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-200/20 to-yellow-200/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <span className="relative z-10 group-hover:scale-110 transition-transform duration-300">
                      {recipe.image}
                    </span>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-1 mb-3">
                      <Badge 
                        variant="outline" 
                        className="text-xs px-2 py-0.5 bg-orange-50 border-orange-200 text-orange-700"
                      >
                        {recipe.category}
                      </Badge>
                      <Badge 
                        variant="outline" 
                        className={`text-xs px-2 py-0.5 ${
                          recipe.difficulty === '쉬움' 
                            ? 'bg-green-50 border-green-200 text-green-700'
                            : recipe.difficulty === '보통'
                            ? 'bg-yellow-50 border-yellow-200 text-yellow-700'
                            : 'bg-red-50 border-red-200 text-red-700'
                        }`}
                      >
                        {recipe.difficulty}
                      </Badge>
                    </div>
                    <h3 className="font-semibold mb-2 text-gray-900 group-hover:text-purple-600 transition-colors line-clamp-1">
                      {recipe.title}
                    </h3>
                    {recipe.description && (
                      <p className="text-xs text-gray-500 mb-2 line-clamp-1">{recipe.description}</p>
                    )}
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="w-3 h-3 mr-1" />
                      <span>{recipe.time}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile View - Show all cards in grid on smaller screens */}
      <div className="md:hidden mt-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {recipes.map((recipe) => (
            <Card key={`mobile-${recipe.id}`} className="group hover:shadow-lg transition-all cursor-pointer overflow-hidden">
              <div className="aspect-video bg-gradient-to-br from-orange-100 to-yellow-100 flex items-center justify-center text-4xl">
                {recipe.image}
              </div>
              <CardContent className="p-3">
                <div className="flex items-center gap-1 mb-2">
                  <Badge variant="outline" className="text-xs">
                    {recipe.category}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {recipe.difficulty}
                  </Badge>
                </div>
                <h3 className="font-semibold mb-1 text-sm">{recipe.title}</h3>
                {recipe.description && (
                  <p className="text-xs text-gray-500 mb-1 line-clamp-1">{recipe.description}</p>
                )}
                <div className="flex items-center text-xs text-gray-500">
                  <Clock className="w-3 h-3 mr-1" />
                  {recipe.time}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}